import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { addProjectionRequestDataToClientPackage } from '../../../utils/clientPackageUtils';
import { showErrorToast } from '../../../utils/toastUtils';
import { useClient } from '../../client-api';
import { type ClientPackageDto, finalizeClientPackage } from '../../generated/client';
import { useProjectionSetup } from '../../projection-api/queries/use-projection-setup';
import { useGeneralProjectionSettings } from '../../projection-settings-api';
import { queryKeys } from '../../query-keys';

export const useFinalizeClientPackage = (): UseMutationResult<ClientPackageDto, AxiosError, ClientPackageDto> => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: generalSettings } = useGeneralProjectionSettings();
    const { data: client } = useClient();
    const projectionSettings = useProjectionSetup(generalSettings?.projectionPeriodYears, client);

    return useMutation({
        mutationFn: (clientPackage) => {
            const updatedClientPackage = addProjectionRequestDataToClientPackage(clientPackage, projectionSettings.body);

            return finalizeClientPackage({ body: updatedClientPackage, path: { id: clientPackage.id } }).then((result) => result.data!);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.clientPackagesForClient(client?.id) });
        },
        onError: (error) => showErrorToast({ error, userMessage: t('failureDocumentDownload') }),
    });
};
